// src/pages/Admin.jsx
// Admin Dashboard / CMS for managing the book catalog.
// Provides Add, Edit, Delete capabilities and hooks up database mutations to Cloudflare APIs.

import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getAuthToken } from "../utils/authToken";
import useSEO from "../hooks/useSEO";

export default function Admin({ books, loading, addBook, updateBook, deleteBook }) {
  useSEO({
    title: "Admin Control Center | Ebenezer Books",
    description: "Manage your library book catalog, view download metrics, upload new PDF titles.",
    ogType: "website"
  });

  const { user } = useAuth();

  // Book management states
  const [activeBook, setActiveBook] = useState(null); // Book being edited/added
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Admin management states
  const [adminEmail, setAdminEmail] = useState("");
  const [adminsList, setAdminsList] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [adminError, setAdminError] = useState("");
  const [adminSuccess, setAdminSuccess] = useState("");

  // Load admins list on mount
  useEffect(() => {
    loadAdminsList();
  }, []);

  async function loadAdminsList() {
    if (!user) return;
    setLoadingAdmins(true);
    try {
      const token = await getAuthToken();
      const res = await fetch("/api/auth/list-admins", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to load admins");
      const result = await res.json();
      setAdminsList(result.admins || []);
    } catch (err) {
      console.error("Error loading admins:", err);
      setAdminError("Failed to load admin list");
    } finally {
      setLoadingAdmins(false);
    }
  }

  async function handleAddAdmin(e) {
    e.preventDefault();
    if (!adminEmail.trim()) {
      setAdminError("Please enter an email");
      return;
    }

    setAdminError("");
    setAdminSuccess("");

    try {
      const token = await getAuthToken();
      const res = await fetch("/api/auth/add-admin", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ email: adminEmail, role: "admin" })
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to add admin");

      setAdminSuccess(`Admin added: ${adminEmail}`);
      setAdminEmail("");
      await loadAdminsList();
    } catch (err) {
      setAdminError(err.message);
    }
  }

  async function handleRemoveAdmin(userId) {
    if (!window.confirm("Remove this user's admin privileges?")) return;

    try {
      const token = await getAuthToken();
      const res = await fetch("/api/auth/remove-admin", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ userId })
      });

      if (!res.ok) throw new Error("Failed to remove admin");
      setAdminSuccess("Admin privileges removed");
      await loadAdminsList();
    } catch (err) {
      setAdminError(err.message);
    }
  }

  // Form states
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [genre, setGenre] = useState("");
  const [year, setYear] = useState(2026);
  const [pages, setPages] = useState(200);
  const [rating, setRating] = useState(4.5);
  const [description, setDescription] = useState("");
  const [badge, setBadge] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [coverFrom, setCoverFrom] = useState("#1F0E1A");
  const [coverTo, setCoverTo] = useState("#8B3A6B");
  const [pdfPath, setPdfPath] = useState("");
  const [epubPath, setEpubPath] = useState("");

  // R2 PDF Uploader states
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadProgress, setUploadProgress] = useState("");

  // R2 EPUB Uploader states
  const [epubUploading, setEpubUploading] = useState(false);
  const [epubUploadError, setEpubUploadError] = useState("");
  const [epubUploadProgress, setEpubUploadProgress] = useState("");

  async function handlePdfUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setUploadError("Only PDF files are supported.");
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadProgress("Uploading PDF to Cloudflare R2...");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = user ? await getAuthToken() : "";
      const res = await fetch("/api/upload", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to upload file.");
      }

      setPdfPath(data.url);
      setUploadProgress(`Successfully uploaded: ${file.name}`);
    } catch (err) {
      setUploadError(err.message);
      setUploadProgress("");
    } finally {
      setUploading(false);
    }
  }

  async function handleEpubUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".epub") && file.type !== "application/epub+zip") {
      setEpubUploadError("Only EPUB files are supported.");
      return;
    }

    setEpubUploading(true);
    setEpubUploadError("");
    setEpubUploadProgress("Uploading EPUB to Cloudflare R2...");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = user ? await getAuthToken() : "";
      const res = await fetch("/api/upload", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to upload file.");
      }

      setEpubPath(data.url);
      setEpubUploadProgress(`Successfully uploaded: ${file.name}`);
    } catch (err) {
      setEpubUploadError(err.message);
      setEpubUploadProgress("");
    } finally {
      setEpubUploading(false);
    }
  }
  // Catalog Stats
  const stats = useMemo(() => {
    const total = books.length;
    const downloads = books.reduce((sum, b) => sum + (b.downloadCount || 0), 0);
    const uniqueGenres = new Set(books.map((b) => b.genre)).size;
    return { total, downloads, uniqueGenres };
  }, [books]);

  // Open modal for adding a new book
  function handleAddOpen() {
    setIsEditMode(false);
    setActiveBook(null);
    setTitle("");
    setAuthor("");
    setGenre("");
    setYear(2026);
    setPages(200);
    setRating(4.5);
    setDescription("");
    setBadge("");
    setTagsInput("");
    setCoverFrom("#1A1208");
    setCoverTo("#C8922A");
    setPdfPath("");
    setUploadError("");
    setUploadProgress("");
    setEpubPath("");
    setEpubUploadError("");
    setEpubUploadProgress("");
    setShowModal(true);
  }

  // Open modal for editing an existing book
  function handleEditOpen(book) {
    setIsEditMode(true);
    setActiveBook(book);
    setTitle(book.title || "");
    setAuthor(book.author || "");
    setGenre(book.genre || "");
    setYear(book.year !== undefined ? book.year : 2026);
    setPages(book.pages || 200);
    setRating(book.rating || 4.5);
    setDescription(book.description || "");
    setBadge(book.badge || "");
    setTagsInput(book.tags ? book.tags.join(", ") : "");
    setCoverFrom(book.coverColor?.[0] || book.colorFrom || "#1A1208");
    setCoverTo(book.coverColor?.[1] || book.colorTo || "#C8922A");
    setPdfPath(book.pdfPath || "");
    setEpubPath(book.epubPath || "");
    setUploadError("");
    setUploadProgress("");
    setEpubUploadError("");
    setEpubUploadProgress("");
    setShowModal(true);
  }


  // Handle Form Submission (Add or Update)
  async function handleSubmit(e) {
    e.preventDefault();

    if (!title.trim() || !author.trim() || !genre.trim()) {
      alert("Please fill in Title, Author, and Genre.");
      return;
    }

    if (!pdfPath && !epubPath) {
      alert("Please upload at least a PDF file or an EPUB file for this book.");
      return;
    }

    const tags = tagsInput
      ? tagsInput.split(",").map((t) => t.trim()).filter((t) => t.length > 0)
      : [];

    const bookData = {
      title,
      author,
      genre,
      year: Number(year),
      pages: Number(pages),
      rating: Number(rating),
      description,
      badge: badge.trim() || null,
      tags,
      coverColor: [coverFrom, coverTo],
      pdfPath,
      epubPath,
    };

    try {
      if (isEditMode && activeBook) {
        // Update via Cloudflare API
        await updateBook(activeBook.id, bookData);
      } else {
        // Add via Cloudflare API
        await addBook(bookData);
      }
      setShowModal(false);
    } catch (err) {
      alert(`Operation failed: ${err.message}`);
    }
  }

  // Delete Book
  async function handleDelete(bookId, bookTitle) {
    if (!window.confirm(`Are you sure you want to delete "${bookTitle}"?`)) return;

    try {
      await deleteBook(bookId);
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  }

  return (
    <div style={{ background: "var(--cream)", minHeight: "calc(100vh - var(--nav-h))", padding: "2rem", color: "var(--ink)", transition: "background 0.3s ease, color 0.3s ease" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        
        {/* Header Section */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
          <div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.2rem", fontWeight: 600, color: "var(--ink)", margin: 0 }}>
              Admin Control Center
            </h1>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "var(--muted)", marginTop: "4px" }}>
              Secure management portal for updating the Ebenezer Books library.
            </p>
          </div>
          <button
            onClick={handleAddOpen}
            style={{
              background: "var(--ink)",
              color: "var(--cream)",
              border: "none",
              borderRadius: "8px",
              padding: "10px 22px",
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 500,
              fontSize: "0.9rem",
              cursor: "pointer",
              boxShadow: "var(--shadow)",
              transition: "background 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--gold)";
              e.currentTarget.style.color = "var(--cream)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--ink)";
              e.currentTarget.style.color = "var(--cream)";
            }}
          >
            + Add New Book
          </button>
        </div>

        {/* Stats Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.5rem", marginBottom: "2.5rem" }}>
          {[
            { value: stats.total, label: "Total Books Loaded" },
            { value: stats.downloads, label: "Total PDF Downloads" },
            { value: stats.uniqueGenres, label: "Active Book Genres" },
          ].map((st, i) => (
            <div
              key={i}
              style={{
                background: "var(--white)",
                border: "1px solid var(--dust)",
                borderRadius: "12px",
                padding: "1.5rem",
                boxShadow: "var(--shadow)",
                transition: "background 0.3s ease, border-color 0.3s ease",
              }}
            >
              <div style={{ fontSize: "0.72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>
                {st.label}
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.5rem", fontWeight: 700, color: "var(--gold)", marginTop: "8px" }}>
                {loading ? "..." : st.value}
              </div>
            </div>
          ))}
        </div>

        {/* Main Books Catalog Table */}
        <div style={{ background: "var(--white)", border: "1px solid var(--dust)", borderRadius: "12px", overflow: "hidden", boxShadow: "var(--shadow)", transition: "background 0.3s ease, border-color 0.3s ease" }}>
          <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid var(--dust)" }}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.3rem", fontWeight: 600, color: "var(--ink)", margin: 0 }}>
              Book Library Inventory
            </h2>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "4rem 0", color: "var(--muted)", fontFamily: "'Outfit', sans-serif" }}>
              Loading Catalog Inventory...
            </div>
          ) : books.length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem 0", color: "var(--muted)", fontFamily: "'Outfit', sans-serif" }}>
              No books found in the repository. Add your first book above!
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", textAlign: "left" }}>
                <thead>
                  <tr style={{ background: "var(--cream)", borderBottom: "1px solid var(--dust)", color: "var(--muted)", fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    <th style={{ padding: "1rem 1.5rem" }}>Book Title / Author</th>
                    <th style={{ padding: "1rem 1.5rem" }}>Genre</th>
                    <th style={{ padding: "1rem 1.5rem" }}>Year</th>
                    <th style={{ padding: "1rem 1.5rem" }}>Pages</th>
                    <th style={{ padding: "1rem 1.5rem" }}>Downloads</th>
                    <th style={{ padding: "1rem 1.5rem", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {books.map((b) => (
                    <tr key={b.id} style={{ borderBottom: "1px solid var(--dust)", transition: "background 0.2s" }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--cream)")} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
                      <td style={{ padding: "1rem 1.5rem", display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{
                          width: "36px", height: "48px",
                          background: `linear-gradient(160deg, ${b.coverColor?.[0] || "#1a1208"}, ${b.coverColor?.[1] || "#C8922A"})`,
                          borderRadius: "4px",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "12px", flexShrink: 0,
                          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                        }}>📖</div>
                        <div>
                          <div style={{ fontWeight: 600, color: "var(--ink)" }}>{b.title}</div>
                          <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "2px" }}>by {b.author}</div>
                        </div>
                      </td>
                      <td style={{ padding: "1rem 1.5rem" }}>
                        <span style={{ fontSize: "0.75rem", background: "rgba(200,146,42,0.08)", color: "var(--gold)", padding: "4px 10px", borderRadius: "12px", fontWeight: 500 }}>
                          {b.genre}
                        </span>
                      </td>
                      <td style={{ padding: "1rem 1.5rem" }}>{b.year < 0 ? `${Math.abs(b.year)} BC` : b.year}</td>
                      <td style={{ padding: "1rem 1.5rem" }}>{b.pages}</td>
                      <td style={{ padding: "1rem 1.5rem", fontWeight: 500 }}>⬇ {b.downloadCount || 0}</td>
                      <td style={{ padding: "1rem 1.5rem", textAlign: "right" }}>
                        <button
                          onClick={() => handleEditOpen(b)}
                          style={{
                            background: "none", border: "none", color: "var(--gold)",
                            marginRight: "15px", cursor: "pointer", fontWeight: 500, fontSize: "0.85rem"
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(b.id, b.title)}
                          style={{
                            background: "none", border: "none", color: "var(--rust)",
                            cursor: "pointer", fontWeight: 500, fontSize: "0.85rem"
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Admin Management Section */}
        <div style={{ marginTop: "2.5rem", background: "var(--white)", border: "1px solid var(--dust)", borderRadius: "12px", overflow: "hidden", boxShadow: "var(--shadow)" }}>
          <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid var(--dust)", background: "var(--cream)" }}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.3rem", fontWeight: 600, color: "var(--ink)", margin: 0 }}>
              👥 Admin Management
            </h2>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.85rem", color: "var(--muted)", margin: "4px 0 0 0" }}>
              Add or remove admin privileges for team members
            </p>
          </div>

          <div style={{ padding: "1.5rem" }}>
            {/* Add Admin Form */}
            <form onSubmit={handleAddAdmin} style={{ display: "flex", gap: "10px", marginBottom: "1.5rem", fontFamily: "'Outfit', sans-serif" }}>
              <div style={{ flex: 1 }}>
                <input
                  type="email"
                  placeholder="Enter email to make admin"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid var(--dust)",
                    borderRadius: "6px",
                    fontSize: "0.9rem",
                    background: "var(--cream)",
                    color: "var(--ink)"
                  }}
                />
              </div>
              <button
                type="submit"
                style={{
                  background: "var(--ink)",
                  color: "var(--cream)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 22px",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  transition: "background 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--gold)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--ink)";
                }}
              >
                Grant Admin
              </button>
            </form>

            {/* Messages */}
            {adminError && (
              <div style={{ padding: "10px 12px", background: "var(--error-faint)", color: "var(--rust)", border: "1px solid var(--rust)", borderRadius: "6px", fontSize: "0.85rem", marginBottom: "1rem", fontFamily: "'Outfit', sans-serif" }}>
                ⚠️ {adminError}
              </div>
            )}
            {adminSuccess && (
              <div style={{ padding: "10px 12px", background: "var(--success-faint)", color: "var(--success)", border: "1px solid var(--success)", borderRadius: "6px", fontSize: "0.85rem", marginBottom: "1rem", fontFamily: "'Outfit', sans-serif" }}>
                ✓ {adminSuccess}
              </div>
            )}

            {/* Admins List */}
            <div>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.95rem", fontWeight: 600, color: "var(--ink)", marginBottom: "1rem" }}>
                Current Admins ({adminsList.length})
              </h3>

              {loadingAdmins ? (
                <div style={{ color: "var(--muted)", fontSize: "0.9rem" }}>Loading...</div>
              ) : adminsList.length === 0 ? (
                <div style={{ color: "var(--muted)", fontSize: "0.9rem" }}>No admins yet</div>
              ) : (
                <div style={{ display: "grid", gap: "10px" }}>
                  {adminsList.map((admin) => (
                    <div
                      key={admin.user_id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px",
                        background: "var(--cream)",
                        border: "1px solid var(--dust)",
                        borderRadius: "6px",
                        fontFamily: "'Outfit', sans-serif"
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: "0.9rem" }}>{admin.email}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "2px" }}>
                          Role: <span style={{ fontWeight: 500, color: "var(--gold)" }}>{admin.role}</span>
                        </div>
                      </div>
                      {admin.user_id !== user?.uid && (
                        <button
                          onClick={() => handleRemoveAdmin(admin.user_id)}
                          style={{
                            background: "var(--rust)",
                            color: "var(--cream)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "6px 12px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            transition: "opacity 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = "0.8";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = "1";
                          }}
                        >
                          Remove
                        </button>
                      )}
                      {admin.user_id === user?.uid && (
                        <span style={{ fontSize: "0.75rem", background: "var(--gold)", color: "var(--cream)", padding: "4px 10px", borderRadius: "4px", fontWeight: 500 }}>
                          You
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {showModal && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem"
          }}>
            <div style={{
              background: "var(--white)", width: "100%", maxWidth: "680px", maxHeight: "90vh",
              borderRadius: "16px", overflowY: "auto", boxShadow: "var(--shadow-lg)",
              display: "flex", flexDirection: "column", animation: "fadeUp 0.3s",
              color: "var(--ink)",
              transition: "background 0.3s ease, color 0.3s ease"
            }}>
              
              {/* Modal Header */}
              <div style={{ padding: "1.25rem 2rem", borderBottom: "1px solid var(--dust)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.5rem", fontWeight: 600, color: "var(--ink)", margin: 0 }}>
                  {isEditMode ? "Edit Book Details" : "Add New Book to Catalog"}
                </h3>
                <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "var(--muted)" }}>✕</button>
              </div>

              {/* Modal Body / Form */}
              <form onSubmit={handleSubmit} style={{ padding: "2rem", display: "flex", flexDirection: "column", gap: "1.25rem", fontFamily: "'Outfit', sans-serif" }}>
                
                {/* Title & Author */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Book Title *</label>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Author Name *</label>
                    <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)} required style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                  </div>
                </div>

                {/* Genre, Year, Pages, Rating */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Genre *</label>
                    <input type="text" value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="e.g. Theology" required style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Year</label>
                    <input type="number" value={year} onChange={(e) => setYear(e.target.value)} style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Pages</label>
                    <input type="number" value={pages} onChange={(e) => setPages(e.target.value)} style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Rating (0-5)</label>
                    <input type="number" step="0.1" min="0" max="5" value={rating} onChange={(e) => setRating(e.target.value)} style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Book Description</label>
                  <textarea rows="3" value={description} onChange={(e) => setDescription(e.target.value)} style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", resize: "vertical", fontFamily: "inherit", background: "var(--cream)", color: "var(--ink)" }} />
                </div>

                {/* Tags & Badge */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Tags (comma-separated)</label>
                    <input type="text" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="e.g. Apologetics, Faith, Grace" style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Promo Badge</label>
                    <input type="text" value={badge} onChange={(e) => setBadge(e.target.value)} placeholder="e.g. Featured, Classic" style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                  </div>
                </div>

                {/* Cover Colors Start / End */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Cover Color Gradient (Start)</label>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <input type="color" value={coverFrom} onChange={(e) => setCoverFrom(e.target.value)} style={{ width: "42px", height: "42px", padding: 0, border: "1px solid var(--dust)", borderRadius: "6px", cursor: "pointer", background: "transparent" }} />
                      <input type="text" value={coverFrom} onChange={(e) => setCoverFrom(e.target.value)} style={{ flex: 1, padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Cover Color Gradient (End)</label>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <input type="color" value={coverTo} onChange={(e) => setCoverTo(e.target.value)} style={{ width: "42px", height: "42px", padding: 0, border: "1px solid var(--dust)", borderRadius: "6px", cursor: "pointer", background: "transparent" }} />
                      <input type="text" value={coverTo} onChange={(e) => setCoverTo(e.target.value)} style={{ flex: 1, padding: "10px 14px", border: "1px solid var(--dust)", borderRadius: "6px", fontSize: "0.9rem", background: "var(--cream)", color: "var(--ink)" }} />
                    </div>
                  </div>
                </div>

                {/* Document Uploaders */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
                  {/* PDF Document Uploader */}
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Book PDF Document</label>
                    
                    {pdfPath ? (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        background: "var(--cream)",
                        border: "1px solid var(--dust)",
                        borderRadius: "6px",
                        fontSize: "0.9rem"
                      }}>
                        <span style={{ color: "var(--sage)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
                          ✓ PDF: {pdfPath.split("key=")[1] ? decodeURIComponent(pdfPath.split("key=")[1]) : "PDF Uploaded"}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setPdfPath("");
                            setUploadProgress("");
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--rust)",
                            cursor: "pointer",
                            fontSize: "0.78rem",
                            fontWeight: 600
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handlePdfUpload}
                          style={{ display: "none" }}
                          id="admin-pdf-upload"
                        />
                        <label
                          htmlFor="admin-pdf-upload"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "1.5rem 1rem",
                            border: "2px dashed var(--dust)",
                            borderRadius: "12px",
                            cursor: "pointer",
                            background: "var(--white)",
                            transition: "border-color 0.2s, background 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "var(--gold)";
                            e.currentTarget.style.background = "var(--cream)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "var(--dust)";
                            e.currentTarget.style.background = "var(--white)";
                          }}
                        >
                          <span style={{ fontSize: "1.5rem", marginBottom: "0.3rem" }}>📁</span>
                          <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--ink)", textAlign: "center" }}>
                            Choose PDF to Upload
                          </span>
                        </label>
                      </div>
                    )}

                    {/* PDF Uploading Status / Error messages */}
                    {uploading && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px", fontSize: "0.8rem", color: "var(--gold)" }}>
                        <div style={{ width: "12px", height: "12px", border: "2px solid var(--dust)", borderTopColor: "var(--gold)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                        <span>{uploadProgress}</span>
                      </div>
                    )}
                    {uploadError && (
                      <div style={{ marginTop: "8px", fontSize: "0.8rem", color: "var(--rust)", fontWeight: 500 }}>
                        ⚠️ {uploadError}
                      </div>
                    )}
                    {!uploading && !uploadError && uploadProgress && (
                      <div style={{ marginTop: "8px", fontSize: "0.8rem", color: "var(--sage)", fontWeight: 500 }}>
                        {uploadProgress}
                      </div>
                    )}
                  </div>

                  {/* EPUB Document Uploader */}
                  <div>
                    <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: "var(--muted)", marginBottom: "6px" }}>Book EPUB Document</label>
                    
                    {epubPath ? (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        background: "var(--cream)",
                        border: "1px solid var(--dust)",
                        borderRadius: "6px",
                        fontSize: "0.9rem"
                      }}>
                        <span style={{ color: "var(--sage)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
                          ✓ EPUB: {epubPath.split("key=")[1] ? decodeURIComponent(epubPath.split("key=")[1]) : "EPUB Uploaded"}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setEpubPath("");
                            setEpubUploadProgress("");
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--rust)",
                            cursor: "pointer",
                            fontSize: "0.78rem",
                            fontWeight: 600
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept=".epub,application/epub+zip"
                          onChange={handleEpubUpload}
                          style={{ display: "none" }}
                          id="admin-epub-upload"
                        />
                        <label
                          htmlFor="admin-epub-upload"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "1.5rem 1rem",
                            border: "2px dashed var(--dust)",
                            borderRadius: "12px",
                            cursor: "pointer",
                            background: "var(--white)",
                            transition: "border-color 0.2s, background 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "var(--gold)";
                            e.currentTarget.style.background = "var(--cream)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "var(--dust)";
                            e.currentTarget.style.background = "var(--white)";
                          }}
                        >
                          <span style={{ fontSize: "1.5rem", marginBottom: "0.3rem" }}>📖</span>
                          <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--ink)", textAlign: "center" }}>
                            Choose EPUB to Upload
                          </span>
                        </label>
                      </div>
                    )}

                    {/* EPUB Uploading Status / Error messages */}
                    {epubUploading && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px", fontSize: "0.8rem", color: "var(--gold)" }}>
                        <div style={{ width: "12px", height: "12px", border: "2px solid var(--dust)", borderTopColor: "var(--gold)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                        <span>{epubUploadProgress}</span>
                      </div>
                    )}
                    {epubUploadError && (
                      <div style={{ marginTop: "8px", fontSize: "0.8rem", color: "var(--rust)", fontWeight: 500 }}>
                        ⚠️ {epubUploadError}
                      </div>
                    )}
                    {!epubUploading && !epubUploadError && epubUploadProgress && (
                      <div style={{ marginTop: "8px", fontSize: "0.8rem", color: "var(--sage)", fontWeight: 500 }}>
                        {epubUploadProgress}
                      </div>
                    )}
                  </div>
                  
                  {/* Inline spin animation styling */}
                  <style>{`
                    @keyframes spin {
                      to { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>

                {/* Submit Action buttons */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "1rem", borderTop: "1px solid var(--dust)", paddingTop: "1.5rem" }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{
                      background: "transparent", color: "var(--muted)", border: "1px solid var(--dust)",
                      borderRadius: "6px", padding: "10px 20px", cursor: "pointer", fontSize: "0.9rem"
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      background: "var(--ink)",
                      color: "var(--cream)",
                      border: "none",
                      borderRadius: "6px",
                      padding: "10px 24px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      transition: "background 0.2s, color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--gold)";
                      e.currentTarget.style.color = "var(--cream)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "var(--ink)";
                      e.currentTarget.style.color = "var(--cream)";
                    }}
                  >
                    {isEditMode ? "Save Changes" : "Create Book"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
